import { format } from 'date-fns'
import { Check, X } from 'lucide-react'
import React from 'react'

import { Badge } from 'src/shared/components/ui/miscellaneous/badge'
import type { Referral, ReferralData } from 'src/shared/schemas/referrals/referral'
import type { ReferralField } from 'src/shared/schemas/referrals/referral-field-definitions'
import type { ReferralShare } from 'src/shared/schemas/referrals/referral-share'
import { formatCurrencyWithDollarSign } from 'src/shared/utils/format-currency'

// Instead of importing Lead directly, define a common interface for address data
type AddressData = {
  propertyStreetAddress1?: string
  propertyStreetAddress2?: string
  propertyCity?: string
  propertyState?: string
  propertyZip?: string
  desiredLocations?: string[]
  details?: AddressData
}

export const formatPrintedReferralAddress = (
  data: Referral | ReferralShare | AddressData,
  isPrivate = false,
): string => {
  // For a Lead, we need to check if the address data is in the details property
  const addressData = 'details' in data && data.details ? (data.details as AddressData) : (data as AddressData)

  if (addressData.propertyStreetAddress1) {
    if (isPrivate) {
      return `${addressData.propertyCity}, ${addressData.propertyState}`
    } else {
      return `${addressData.propertyStreetAddress1}${
        addressData.propertyStreetAddress2 ? `, ${addressData.propertyStreetAddress2}` : ''
      }, ${addressData.propertyCity}, ${addressData.propertyState}, ${addressData.propertyZip}`
    }
  } else if (addressData.desiredLocations && addressData.desiredLocations.length > 0) {
    return addressData.desiredLocations.length > 1
      ? `${addressData.desiredLocations[0]} + ${addressData.desiredLocations.length - 1} more`
      : addressData.desiredLocations[0]
  } else {
    return 'Address TBD'
  }
}

// Add this utility function for formatting price information
export const formatPriceInformation = (data: any): React.ReactNode => {
  // Check for exactPrice first
  if (data.exactPrice) {
    return <>{formatCurrencyWithDollarSign(data.exactPrice)}</>
  }

  // Then check for priceRange
  if (data.priceRange?.min && data.priceRange?.max) {
    return (
      <>
        {formatCurrencyWithDollarSign(data.priceRange.min)} - {formatCurrencyWithDollarSign(data.priceRange.max)}
      </>
    )
  }

  // Then check for budget
  if (data.budget?.min && data.budget?.max) {
    return (
      <>
        {formatCurrencyWithDollarSign(data.budget.min)} - {formatCurrencyWithDollarSign(data.budget.max)}
      </>
    )
  }

  return null
}

// Check if any price information exists
export const hasPriceInformation = (data: any): boolean => {
  return !!data.exactPrice || (data.priceRange?.min && data.priceRange?.max) || (data.budget?.min && data.budget?.max)
}

export function formatFieldValue(field: ReferralField, value: any, referralData?: ReferralData): React.ReactNode {
  switch (field.type) {
    case 'phone':
      return formatPhone(value)
    case 'currency':
      return formatCurrency(value)
    case 'enum':
      return formatEnum(field, value, referralData)
    case 'list':
      return formatList(value)
    case 'text':
      return formatText(value)
    case 'date':
      return formatDate(value)
    case 'boolean':
      return formatBoolean(value)
    case 'priceRange':
      return formatCurrency(value.min) + ' - ' + formatCurrency(value.max)
    default:
      return formatString(value)
  }
}

function formatText(value: string): React.ReactNode {
  if (!value) return null

  // Split text by line breaks and map each line to a paragraph
  const lines = value.split(/\r?\n/)
  return (
    <div className="whitespace-pre-wrap break-words">
      {lines.map((line, index) => (
        <React.Fragment key={index}>
          {line}
          {index < lines.length - 1 && <br />}
        </React.Fragment>
      ))}
    </div>
  )
}

function formatDate(value: string | Date): string {
  if (!value) return ''

  try {
    const date = typeof value === 'string' ? new Date(value) : value
    return format(date, 'MMM d, yyyy')
  } catch (error) {
    console.error('Error formatting date:', error)
    return 'Invalid Date'
  }
}

function formatPhone(phone: string): string {
  const cleaned = ('' + phone).replace(/\D/g, '')
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/)
  return match ? `(${match[1]}) ${match[2]}-${match[3]}` : phone
}

function formatCurrency(amount: number): string {
  return `$${amount.toLocaleString('en-US')}`
}

function formatEnum(field: ReferralField, key: string, referralData?: ReferralData): string {
  // Handle dynamic maps that depend on other field values
  const map = field.getMap ? field.getMap(referralData) : field.map

  if (!map) {
    console.warn(`Field map is not defined for field: ${field.label}`)
    return 'Unknown'
  }
  if (!map[key]) {
    console.warn(`Key "${key}" not found in field map for field: ${field.label}`)
    return 'Unknown'
  }
  return map[key].label
}

function formatList(list: string[]): React.ReactNode {
  if (!list?.length) return null
  return (
    <div className="flex flex-wrap gap-2">
      {list.map((item, index) => (
        <Badge variant="accent" key={index} text={item} />
      ))}
    </div>
  )
}

function formatString(value: string): React.ReactNode {
  if (!value) return null
  return <span className="break-words">{value}</span>
}

function formatBoolean(value: boolean): React.ReactNode {
  return (
    <div className="flex items-center">
      {value ? <Check className="size-5 text-primary" /> : <X className="size-5 text-destructive" />}
      <span className="ml-2">{value ? 'Yes' : 'No'}</span>
    </div>
  )
}
